/*
 * ServicePartnerList.tsx
 * This Service Partner list Component is used for representing all the saved partners in list Form.
 */
// Import necessary modules and components
import useFetch from "../../../hooks/useFetch";
import { useContext, useEffect, useState } from "react";
import { PageHeader } from "../../../components/page-header/page-header";
import GenericPopover from "../../../components/pop-over";
import useToast from "../../../hooks/useToast";
import Utils from "../../../utils";
import { TFilterModel } from "../../../types/config";
import { ePrivileges, eResultCode } from "../../../utils/enum";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import {
  dateOnlyFormat,
  debounceTime,
  defaultFilterParams,
  PageSize,
} from "../../../utils/constants";
import {
  Button,
  Drawer,
  Dropdown,
  Input,
  Space,
  Table,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import ServicePartnerForm from "./form";
import { TProject } from "../../../types/config";
// import BulkUploadForm from "./bulk-upload/bulk-upload-form";
import CustomDrawer from "../../../components/drawer";
import { GetProjectList } from "../../../utils/api.constant";
import { useNavigate } from "react-router-dom";
import utils from "../../../utils";
import ActionCard from "../../../components/action-card/action-card";
import { UserContext } from "../../../context";
import {
  EditOutlined,
  FilterOutlined,
  IdcardOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import ColumnManager from "../../../components/configurable-columns";
import ConfigParamForm from "./form";
import DateUtils from "../../../utils/date";
import ExportToCSV from "../../../components/export-to-csv/export-to-csv";
import AddEditProjectForm from "./form";

/**
 * ServicePartnerList Component
 * This component renders the list of service partners, with
 * the ability to search, filter, and add new service partners.
 * It also includes a bulk upload feature for importing
 * service partners in bulk.
 *
 * @returns {JSX.Element} The JSX structure for the
 *         ServicePartnerList component.
 */
export default function ProjectList() {
  // State and hooks initialization
  const navigate = useNavigate();
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TProject[]>([]);
  const [filterParams, setFilterParams] =
    useState<TFilterModel>(defaultFilterParams);
  const { onShowToast } = useToast();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<TProject | null>(null);
  const initialColumns: any[] = [
    {
      title: "Sr. No.",
      key: "serialNumber",
      width: "8%",
      render: (_: any, __: TProject, index: number) => index + 1,
    },
    {
      title: "Client Name",
      dataIndex: "clientName",
      key: "clientName",
      width: "20%",
      filterSearch: true,
      filters: [],
      sorter: (a: TProject, b: TProject) =>
        (a.clientName || "")
          .toString()
          .localeCompare((b.clientName || "").toString()),
      onFilter: (value: string | number | boolean, record: TProject) =>
        record.clientName
          ?.toString()
          .toLowerCase()
          .includes(value.toString().toLowerCase()),
    },
    {
      title: "Project Name",
      dataIndex: "name",
      key: "name",
      width: "20%",
      sorter: (a: TProject, b: TProject) =>
        (a.name || "").toString().localeCompare((b.name || "").toString()),
      filterSearch: true,
      filters: [],
      onFilter: (value: string | number | boolean, record: TProject) =>
        record.name
          ?.toString()
          .toLowerCase()
          .includes(value.toString().toLowerCase()),
    },
    {
      title: "Project Category",
      dataIndex: "projectCategoryName",
      key: "projectCategoryName",
      width: "20%",
      filterSearch: true,
      filters: [], // Will be populated dynamically
      sorter: (a: TProject, b: TProject) =>
        a.projectCategoryName
          .toString()
          .localeCompare(b.projectCategoryName.toString()),
      onFilter: (value: string | number | boolean, record: TProject) =>
        record.projectCategoryName
          ?.toString()
          .toLowerCase()
          .includes(value.toString().toLowerCase()),
    },
    {
      title: "Team Members",
      dataIndex: "teamMembers",
      key: "teamMembers",
      width: "20%",
      filterSearch: true,
      filters: utils.getUniqueFilters(data, "teamMembers"),
      sorter: (a: TProject, b: TProject) =>
        a.teamMembers.toString().localeCompare(b.teamMembers.toString()),
      onFilter: (value: string | number | boolean, record: TProject) =>
        record.teamMembers?.toString().includes(value as string),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      key: "startDate",
      width: "15%",
      filterSearch: true,
      sorter: (a: TProject, b: TProject) =>
        String(a.startDate).localeCompare(String(b.startDate)),
      onFilter: (value: string | number | boolean, record: TProject) =>
        DateUtils.format(record.startDate, dateOnlyFormat).includes(
          value as string,
        ),
      render: (startDate: string) => {
        return DateUtils.format(startDate, dateOnlyFormat);
      },
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      key: "endDate",
      width: "15%",
      filterSearch: true,
      sorter: (a: TProject, b: TProject) =>
        String(a.endDate).localeCompare(String(b.endDate)),
      onFilter: (value: string | number | boolean, record: TProject) =>
        DateUtils.format(record.endDate, dateOnlyFormat).includes(
          value as string,
        ),

      render: (endDate: string) => {
        return DateUtils.format(endDate, dateOnlyFormat);
      },
    },
    {
      title: "Action",
      key: "actions",
      align: "left",
      width: "20%",
      render: (item: TProject) => (
        <Dropdown
          trigger={["click"]}
          getPopupContainer={() => document.body}
          menu={{
            items: [
              Utils.isUserHasAccess(
                context.user?.privilegeList,
                ePrivileges.EDITPROJECT,
              ) && {
                key: "edit",
                label: (
                  <CustomDrawer
                    size="large"
                    trigger={
                      <div
                        style={{
                          display: "flex",
                          gap: 6,
                          alignItems: "center",
                        }}
                      >
                        <EditOutlined style={{ fontSize: "12px" }} />
                        Edit
                      </div>
                    }
                    component={AddEditProjectForm}
                    componentProps={{
                      id: item.id,
                      onRefreshList: () => {
                        fetchData();
                      },
                    }}
                    title="Edit Project"
                  />
                ),
              },
            ].filter(Boolean) as any,
          }}
        >
          <MoreOutlined style={{ fontSize: 20, cursor: "pointer" }} />
        </Dropdown>
      ),
    },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const [searchText, setSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [debouncedSearchText, setDebouncedSearchText] = useState(
    defaultFilterParams.searchText,
  );

  const context: any = useContext(UserContext);

  useEffect(() => {
    fetchData();
  }, [debouncedSearchText]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const requestPayload = {
        data: {
          pageSize: PageSize,
          currentPage: 1,
        },
      };

      const response = await post(GetProjectList, requestPayload);
      const { data, dataResponse, filterModel } = response;
      const { returnCode, description } = dataResponse;
      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        const formattedData = data.map((item: TProject, index: number) => ({
          key: (item.id ?? "").toString(),
          name: item.name,
          startDate: item.startDate,
          endDate: item.endDate,
          clientName: item.clientName,
          projectCategoryName: item.projectCategoryName,
          teamMembers: item.teamMembers
            ? item.teamMembers.map((member) => member.fullName).join(", ")
            : "N/A",
          id: item.id,
        }));
        setData(formattedData);
        setFilterParams({ ...(filterModel as TFilterModel) });
        setIsLoading(false);

        const projectNameFilters = Array.from(
          new Set(data.map((item: TProject) => item.name)),
        ).map((name) => ({ text: name, value: name }));

        const projectCategoryNameFilters = Array.from(
          new Set(data.map((item: TProject) => item.projectCategoryName)),
        ).map((projectCategoryName) => ({
          text: projectCategoryName,
          value: projectCategoryName,
        }));

        const clientNameFilters = Array.from(
          new Set(data.map((item: TProject) => item.clientName)),
        ).map((clientName) => ({ text: clientName, value: clientName }));

        const startDateFilters = Array.from(
          new Set(
            data.map((item: TProject) =>
              DateUtils.format(item.startDate, dateOnlyFormat),
            ),
          ),
        ).map((startDate) => ({
          text: startDate,
          value: startDate,
        }));

        const endDateFilters = Array.from(
          new Set(
            data.map((item: TProject) =>
              DateUtils.format(item.endDate, dateOnlyFormat),
            ),
          ),
        ).map((endDate) => ({
          text: endDate,
          value: endDate,
        }));

        setColumns((prevColumns) =>
          prevColumns.map((col) => {
            if (col.key === "name") {
              return { ...col, filters: projectNameFilters };
            }
            if (col.key === "projectCategoryName") {
              return { ...col, filters: projectCategoryNameFilters };
            }
            if (col.key === "clientName") {
              return { ...col, filters: clientNameFilters };
            }
            if (col.key === "startDate") {
              return { ...col, filters: startDateFilters };
            }
            if (col.key === "endDate") {
              return { ...col, filters: endDateFilters };
            }
            return col;
          }),
        );
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error:", error);
      setIsLoading(false);
    }
  };

  // Update search text state
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(filterParams.searchText);
    }, debounceTime); // Set debounce delay to 300 milliseconds

    return () => {
      clearTimeout(timer); // Clear the timer on each update
    };
  }, [filterParams.searchText]);

  /**
   * Updates the dealer with the given values.
   * Sends a request to the API with the given values, and if the response is successful, shows a success toast and fetches the data again.
   * If the response is not successful, shows an error toast with the description from the response.
   * @param {TDealer} values - the dealer values to update.
   */

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any,
  ) => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      }),
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter((item) =>
    Object.values(item).some((val) =>
      val?.toString().toLowerCase().includes(searchText.toLowerCase()),
    ),
  );

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col,
      ),
    );
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  const exportHeaders = columns
    .filter(
      (column) =>
        column.key && (column?.title as string)?.toLowerCase() !== "action",
    )
    .map((column) => ({
      label: column.title,
      key: column.key,
    }));

  const exportData = data.map((user) => ({
    ...user,
  }));

  const handleResetAll = () => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,

        sortOrder: null,
      })),
    );
    // setTableKey((prev) => prev + 1);
    // fetchData(1, 10); // Fetch data with default pagination
  };

  useEffect(() => {
    setColumns(initialColumns);
  }, [context]);

  // Return JSX structure for User Component
  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardOutlined />}
        title={`${filterParams?.totalRows} Total Projects`}
        size="large"
      >
        <Space>
          {Utils.isUserHasAccess(
            context.user?.privilegeList,
            ePrivileges.ADDPROJECT,
          ) && (
            <CustomDrawer
              size="large"
              trigger={
                <Button disabled={isLoading} type="primary">
                  Add
                </Button>
              }
              component={AddEditProjectForm}
              componentProps={{
                id: 0, // Pass necessary props for ServicePartnerForm
                onRefreshList: () => {
                  navigate("/dashboard/project");
                  !Utils.hasDifference(defaultFilterParams, filterParams) &&
                    fetchData();
                  setFilterParams(defaultFilterParams);
                },
              }}
              title="Add New Project"
            />
          )}

          <Input
            placeholder="Filter by keywords"
            value={searchText}
            onChange={handleSearch}
            prefix={
              <SearchOutlined style={{ color: "var( --bs-search-style)" }} />
            }
          />
          <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`conf_params_${DateUtils.format(new Date())}.csv`}
          />
          <Tooltip title="Reset Filters">
            <FilterOutlined onClick={handleResetAll} />
          </Tooltip>
          <Tooltip title="Configure Columns">
            <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
          </Tooltip>
        </Space>
      </PageHeader>

      <div className="tableWrap">
        <Table
          columns={visibleColumns}
          dataSource={filteredData}
          loading={isLoading}
          pagination={{
            position: ["bottomLeft"],
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total: any, range: any[]) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
          }}
          scroll={{
            y: "calc(100vh - 310px)", // Adjust height dynamically
          }}
          onChange={handleTableChange}
          bordered={false}
          size="small"
          sticky
        />
      </div>
      <Drawer
        title="Customize Columns"
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        style={{ transition: "transform 0.3s ease-in-out" }}
      >
        <ColumnManager
          columns={draftColumns}
          initialColumns={initialColumns}
          onDragEnd={handleDragEnd}
          onToggleColumn={toggleColumn}
          onResetColumns={handleResetColumns}
          onSave={handleSaveColumns}
          onCancel={() => setDrawerVisible(false)}
        />
      </Drawer>
    </div>
  );
}
